import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import * as JSON5 from "json5";
import { format as formatDate } from "date-fns";
import { enGB, sk, hu } from "date-fns/locale"; // import all locales we need

import Backend from "i18next-chained-backend";
import HTTPBackend from "i18next-http-backend";
import CacheBackend from "i18next-localstorage-backend";
import LanguageDetector from "i18next-browser-languagedetector";

import { i18nInterceptor } from "./interceptors";
import { Map as LeafletMap } from "leaflet";
import { DATE_FORMAT_LONG, DATE_TIME_FORMAT_LONG } from "./config";

// TODO: this can be better typed
type Locales = { en: Locale; sk: Locale; hu: Locale };
const locales: Locales = { en: enGB, sk, hu }; // used to look up the required locale

const VERSION = "1.1.0";

const CacheBackendOptions = {
  prefix: "i18next_wb_",
  expirationTime: 7 * 24 * 60 * 60 * 1000,
  defaultVersion: VERSION,
  versions: {},
  store: localStorage,
};

const HTTPBackendOptions = {
  loadPath: "/locales/{{lng}}/{{ns}}.json5",
  parse: JSON5.parse,
  allowMultiLoading: false,
  crossDomain: false,
  withCredentials: false,
  overrideMimeType: false,
  queryStringParams: { v: VERSION },
  reloadInterval: false,
};

i18n
  // load translation using xhr -> see /public/locales
  // learn more: https://github.com/i18next/i18next-xhr-backend
  .use(Backend)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .use(LanguageDetector)
  .init(
    {
      fallbackLng: process.env.NODE_ENV === "development" ? undefined : "en",
      debug: process.env.NODE_ENV === "development" && false,
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default

        // with inspiration coming from 🙏: https://dev.to/ekeijl/react-automatic-date-formatting-in-translations-i18next-date-fns-8df
        format: (value, format?, lng?) => {
          const locale = locales[lng as keyof Locales];

          if (format === "date-long") {
            // TODO: can this be done with native Javascript? e.g. Date.prototype.toLocaleDateString
            return formatDate(value, DATE_FORMAT_LONG, {
              locale,
            });
          } else if (format === "date-time-long") {
            // TODO: can this be done with native Javascript? e.g. Date.prototype.toLocaleDateString
            return formatDate(value, DATE_TIME_FORMAT_LONG, {
              locale,
            });
          }

          return value;
        },
      },
      backend: {
        backends: [
          CacheBackend, // primary
          HTTPBackend, // fallback
        ],
        backendOptions: [CacheBackendOptions, HTTPBackendOptions],
      },
    },
    (err, t) => {
      if (err) {
        return console.error("something went wrong loading translations for", i18n.language, err);
      }
      // else
      i18nInterceptor(i18n.language);
      return true;
    }
  );

i18n.on("languageChanged", function (lng) {
  i18nInterceptor(lng);
});
export { i18n };

export function translateLeafletGeoman(map: LeafletMap) {
  const customTranslation = {
    tooltips: {
      placeMarker: i18n.t("leafletGeoman.tooltips.placeMarker"),
      firstVertex: i18n.t("leafletGeoman.tooltips.firstVertex"),
      continueLine: i18n.t("leafletGeoman.tooltips.continueLine"),
      finishLine: i18n.t("leafletGeoman.tooltips.finishLine"),
      finishPoly: i18n.t("leafletGeoman.tooltips.finishPoly"),
      finishRect: i18n.t("leafletGeoman.tooltips.finishRect"),
      startCircle: i18n.t("leafletGeoman.tooltips.startCircle"),
      finishCircle: i18n.t("leafletGeoman.tooltips.finishCircle"),
      placeCircleMarker: i18n.t("leafletGeoman.tooltips.placeCircleMarker"),
    },
    actions: {
      finish: i18n.t("leafletGeoman.actions.finish"),
      cancel: i18n.t("leafletGeoman.actions.cancel"),
      removeLastVertex: i18n.t("leafletGeoman.actions.removeLastVertex"),
    },
    buttonTitles: {
      drawMarkerButton: i18n.t("leafletGeoman.buttonTitles.drawMarkerButton"),
      drawPolyButton: i18n.t("leafletGeoman.buttonTitles.drawPolyButton"),
      drawLineButton: i18n.t("leafletGeoman.buttonTitles.drawLineButton"),
      drawCircleButton: i18n.t("leafletGeoman.buttonTitles.drawCircleButton"),
      drawRectButton: i18n.t("leafletGeoman.buttonTitles.drawRectButton"),
      editButton: i18n.t("leafletGeoman.buttonTitles.editButton"),
      dragButton: i18n.t("leafletGeoman.buttonTitles.dragButton"),
      cutButton: i18n.t("leafletGeoman.buttonTitles.cutButton"),
      deleteButton: i18n.t("leafletGeoman.buttonTitles.deleteButton"),
      drawCircleMarkerButton: i18n.t("leafletGeoman.buttonTitles.drawCircleMarkerButton"),
      snappingButton: i18n.t("leafletGeoman.buttonTitles.snappingButton"),
      pinningButton: i18n.t("leafletGeoman.buttonTitles.pinningButton"),
    },
  };

  // TODO: can this be removed? In upgrade process right now, so can't check ATM
  // // @ts-ignore: incomplete type
  // map.pm.setLang(i18n.language, customTranslation, i18n.language);
  // @ts-ignore: incomplete type
  map.pm.setLang(i18n.language);
}
